import React, { useState, useMemo, useEffect } from 'react';
import { ModalHeader, ModalBody } from 'reactstrap';
import Rodal from '../../../../components/Rodal/Rodal';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronRight,
    faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { VDRFolderView } from '../../../../slices/marketplace/data-room/models/vdr-folder-view.model';
import './VDRMoveCopyFileModal.scss';

interface FolderNode {
    name: string;
    fullPath: string;
    children: FolderNode[];
}

export type VDRMoveCopyMode = 'move' | 'copy';

interface VDRMoveCopyFileModalProps {
    isVisible: boolean;
    mode: VDRMoveCopyMode;
    fileName?: string;
    rootFolder?: VDRFolderView;
    currentFolderPath?: string;
    isProcessing: boolean;
    onClose: () => void;
    onConfirm: (destinationFolderPath: string) => void;
}

const buildFolderTree = (folder?: VDRFolderView): FolderNode | null => {
    if (!folder) {
        return null;
    }

    return {
        name: folder.name,
        fullPath: folder.fullPath,
        children: (folder.subFolders || [])
            .map((subFolder) => buildFolderTree(subFolder))
            .filter((node): node is FolderNode => node !== null),
    };
};

const collectAllPaths = (node: FolderNode, paths: Set<string>) => {
    paths.add(node.fullPath);
    node.children.forEach((child) => collectAllPaths(child, paths));
};

const VDRMoveCopyFileModal: React.FC<VDRMoveCopyFileModalProps> = ({
    isVisible,
    mode,
    fileName,
    rootFolder,
    currentFolderPath,
    isProcessing,
    onClose,
    onConfirm,
}) => {
    const [selectedFolderPath, setSelectedFolderPath] = useState<
        string | null
    >(null);
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
        new Set()
    );

    const folderTree = useMemo(() => buildFolderTree(rootFolder), [
        rootFolder,
    ]);

    useEffect(() => {
        if (isVisible) {
            setSelectedFolderPath(null);
            const allPaths = new Set<string>();
            if (folderTree) {
                collectAllPaths(folderTree, allPaths);
            }
            setExpandedFolders(allPaths);
        }
    }, [isVisible, folderTree]);

    const toggleFolder = (path: string) => {
        setExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(path)) {
                next.delete(path);
            } else {
                next.add(path);
            }
            return next;
        });
    };

    const renderFolderNode = (node: FolderNode, level: number): JSX.Element => {
        const isExpanded = expandedFolders.has(node.fullPath);
        const hasChildren = node.children.length > 0;
        const isSelected = selectedFolderPath === node.fullPath;
        const isCurrentFolder =
            mode === 'move' && node.fullPath === currentFolderPath;

        return (
            <div key={node.fullPath}>
                <div
                    className={
                        isCurrentFolder
                            ? 'folder-item folder-item-disabled'
                            : 'folder-item'
                    }
                    style={{ paddingLeft: `${level * 20}px` }}>
                    <span className="folder-content">
                        {hasChildren ? (
                            <FontAwesomeIcon
                                icon={isExpanded ? faChevronDown : faChevronRight}
                                className="folder-chevron"
                                onClick={() => toggleFolder(node.fullPath)}
                            />
                        ) : (
                            <span className="folder-chevron-spacer"></span>
                        )}
                        <span className="folder-name">
                            {node.name}
                            {isCurrentFolder && (
                                <span className="folder-current-label">
                                    (current location)
                                </span>
                            )}
                        </span>
                    </span>
                    <button
                        className="btn btn-primary select-folder-btn"
                        disabled={isCurrentFolder}
                        onClick={() => setSelectedFolderPath(node.fullPath)}>
                        {isSelected ? 'Selected' : 'Select'}
                    </button>
                </div>

                {isExpanded && hasChildren && (
                    <>
                        {node.children.map((child) =>
                            renderFolderNode(child, level + 1)
                        )}
                    </>
                )}
            </div>
        );
    };

    const handleClose = () => {
        setSelectedFolderPath(null);
        onClose();
    };

    const isFormValid = selectedFolderPath !== null;
    const actionLabel = mode === 'move' ? 'Move' : 'Copy';

    return (
        <div className="vdr-move-copy-file-modal-container">
            <Rodal
                visible={isVisible}
                onClose={handleClose}
                animation="slideUp"
                className="vdr-move-copy-file-modal"
                showMask={true}
                width={650}
                showCloseButton={true}>
                <ModalHeader>
                    <b>
                        {actionLabel} File{fileName ? `: ${fileName}` : ''}
                    </b>
                </ModalHeader>
                <ModalBody>
                    <div className="vdr-move-copy-form">
                        <div className="form-group">
                            <label>Select Destination Folder</label>
                            <div className="folder-tree-container">
                                {folderTree
                                    ? renderFolderNode(folderTree, 0)
                                    : null}
                            </div>
                        </div>

                        <div className="buttons-container">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleClose}>
                                Cancel
                            </button>
                            <ButtonLoader
                                buttonText={actionLabel}
                                loaderButtonText=""
                                isLoading={isProcessing}
                                disabled={!isFormValid || isProcessing}
                                onClick={() =>
                                    selectedFolderPath &&
                                    onConfirm(selectedFolderPath)
                                }
                                className="btn btn-primary"
                            />
                        </div>
                    </div>
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default VDRMoveCopyFileModal;
